import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  CardRecord,
  ReviewInput,
  ReviewRecord,
  Snapshot,
  StorageBackend,
} from './backend';
import type { CardKey, Skill } from '../../types';
import { t } from '../../strings';

/**
 * The reviews store predates the `id` field and still uses an autoIncrement
 * key, so records are stored without one and the numeric key is surfaced as
 * `id` on read. Ids are therefore local to this database — the export format
 * treats them as opaque and import assigns fresh ones.
 */
interface SyrinxDB extends DBSchema {
  cards: {
    key: CardKey;
    value: CardRecord;
    indexes: { 'by-due': Date; 'by-skill': Skill };
  };
  reviews: {
    key: number;
    value: Omit<ReviewRecord, 'id'>;
    indexes: { 'by-ts': number };
  };
  settings: {
    key: string;
    value: { k: string; v: unknown };
  };
}

const DB_NAME = 'syrinx';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<SyrinxDB>> | null = null;

export function db(): Promise<IDBPDatabase<SyrinxDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SyrinxDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const cards = database.createObjectStore('cards', { keyPath: 'key' });
        cards.createIndex('by-due', 'due');
        cards.createIndex('by-skill', 'skill');

        const reviews = database.createObjectStore('reviews', {
          autoIncrement: true,
        });
        reviews.createIndex('by-ts', 'ts');

        database.createObjectStore('settings', { keyPath: 'k' });
      },
    });
  }
  return dbPromise;
}

/** Progress in the browser's own IndexedDB. No account, never leaves the device. */
export class IdbBackend implements StorageBackend {
  readonly label = t('app.backend.device');

  async hydrate(sinceTs: number): Promise<Snapshot> {
    const d = await db();
    const [cards, settingRows] = await Promise.all([
      d.getAll('cards'),
      d.getAll('settings'),
    ]);
    const settings: Record<string, unknown> = {};
    for (const row of settingRows) settings[row.k] = row.v;
    return {
      cards,
      reviews: await this.readReviews(IDBKeyRange.lowerBound(sinceTs)),
      settings,
    };
  }

  async putCard(rec: CardRecord): Promise<void> {
    await (await db()).put('cards', rec);
  }

  async deleteCard(key: CardKey): Promise<void> {
    await (await db()).delete('cards', key);
  }

  async logReview(rec: ReviewInput): Promise<ReviewRecord> {
    const key = await (await db()).add('reviews', rec);
    return { ...rec, id: String(key) };
  }

  /** Ids here are the store's own autoIncrement keys, surfaced as strings. */
  async deleteReview(id: string): Promise<void> {
    const key = Number(id);
    if (!Number.isFinite(key)) return;
    await (await db()).delete('reviews', key);
  }

  /** Settings are key/value rows here, so a save rewrites each entry. */
  async saveSettings(settings: Record<string, unknown>): Promise<void> {
    const tx = (await db()).transaction('settings', 'readwrite');
    for (const [k, v] of Object.entries(settings)) await tx.store.put({ k, v });
    await tx.done;
  }

  allReviews(): Promise<ReviewRecord[]> {
    return this.readReviews(undefined);
  }

  async replaceAll(snap: Snapshot): Promise<void> {
    const d = await db();
    const tx = d.transaction(['cards', 'reviews', 'settings'], 'readwrite');
    await Promise.all([
      tx.objectStore('cards').clear(),
      tx.objectStore('reviews').clear(),
      tx.objectStore('settings').clear(),
    ]);
    for (const c of snap.cards) await tx.objectStore('cards').put(c);
    for (const r of snap.reviews) {
      const { id: _id, ...rest } = r;
      await tx.objectStore('reviews').add(rest);
    }
    for (const [k, v] of Object.entries(snap.settings)) {
      await tx.objectStore('settings').put({ k, v });
    }
    await tx.done;
  }

  /** Zip the autoIncrement keys back onto the values as `id`. */
  private async readReviews(range: IDBKeyRange | undefined): Promise<ReviewRecord[]> {
    const d = await db();
    const [keys, values] = await Promise.all([
      d.getAllKeysFromIndex('reviews', 'by-ts', range),
      d.getAllFromIndex('reviews', 'by-ts', range),
    ]);
    return values.map((v, i) => ({ ...v, id: String(keys[i]) }));
  }
}
